import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  MessageSquare, Plus, Search, Trash2, Loader2, ClipboardList, Send,
  Inbox, MailOpen, CheckCircle2, X, Mail,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("es-CR", { dateStyle: "medium", timeStyle: "short" });
}

export default function Mensajes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("recibidos"); // recibidos | enviados | todos
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ titulo: "", contenido: "", destinatarios: [] });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/mensajes");
      setItems(r.data || []);
    } catch (_) {
      toast.error("Error cargando mensajes");
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const u = await api.get("/users").catch(() => ({ data: [] }));
        setUsers(u.data || []);
      } catch (_) {}
      await load();
      setLoading(false);
    })();
  }, []);

  const marcarLeido = async (m) => {
    if (m.leido || m.es_remitente) return;
    try {
      await api.post(`/mensajes/${m.id}/leido`);
      load();
    } catch (_) {}
  };

  const eliminar = async (m) => {
    if (!window.confirm(`¿Eliminar el mensaje "${m.titulo}"?`)) return;
    try {
      await api.delete(`/mensajes/${m.id}`);
      toast.success("Mensaje eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sin permiso");
    }
  };

  const enviar = async () => {
    if (!form.titulo.trim() || form.destinatarios.length === 0) {
      toast.error("Título y al menos un destinatario"); return;
    }
    setSaving(true);
    try {
      await api.post("/mensajes", { ...form, tipo: "manual" });
      toast.success("Mensaje enviado");
      setDialog(false);
      setForm({ titulo: "", contenido: "", destinatarios: [] });
      window.dispatchEvent(new Event("mensajes:refresh"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    let arr = items;
    if (tab === "recibidos") arr = arr.filter((m) => !m.es_remitente);
    if (tab === "enviados") arr = arr.filter((m) => m.es_remitente);
    if (q.trim()) {
      const qq = q.trim().toLowerCase();
      arr = arr.filter((m) =>
        (m.titulo || "").toLowerCase().includes(qq) ||
        (m.contenido || "").toLowerCase().includes(qq) ||
        (m.remitente_nombre || "").toLowerCase().includes(qq)
      );
    }
    return arr;
  }, [items, tab, q]);

  const counts = {
    recibidos: items.filter((m) => !m.es_remitente).length,
    no_leidos: items.filter((m) => !m.es_remitente && !m.leido).length,
    enviados: items.filter((m) => m.es_remitente).length,
    todos: items.length,
  };

  const toggleDest = (uid) => {
    setForm((f) => ({
      ...f,
      destinatarios: f.destinatarios.includes(uid)
        ? f.destinatarios.filter((x) => x !== uid)
        : [...f.destinatarios, uid],
    }));
  };

  return (
    <div className="space-y-6" data-testid="module-mensajes">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center relative">
            <MessageSquare className="h-5 w-5 text-[#7A1E2E]" strokeWidth={1.8}/>
            {counts.no_leidos > 0 && (
              <span className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#FBF9F4]">
                {counts.no_leidos}
              </span>
            )}
          </div>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#1A0810]">Mensajes</h1>
            <p className="mt-1 text-sm text-[#4a3838]">Canal de comunicación interna del bufete.</p>
          </div>
        </div>
        <Button data-testid="btn-nuevo-mensaje" onClick={() => setDialog(true)} className="h-11 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]">
          <Send className="h-4 w-4 mr-2" strokeWidth={2}/> Nuevo mensaje
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1 bg-white rounded-lg border border-[#E5DFD3] p-1">
          {[
            { key: "recibidos", label: "Recibidos", count: counts.recibidos, Icon: Inbox },
            { key: "enviados",  label: "Enviados",  count: counts.enviados,  Icon: Send },
            { key: "todos",     label: "Todos",     count: counts.todos,     Icon: Mail },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`tab-mensajes-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3.5 py-1.5 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition-colors ${
                tab === t.key ? "bg-[#F5EEE0] text-[#7A1E2E]" : "text-[#6B5D57] hover:text-[#1A0810]"
              }`}
            >
              <t.Icon className="h-3.5 w-3.5" strokeWidth={2}/>
              {t.label}
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-[#F5EEE0] text-[#6B5D57]">{t.count}</span>
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8}/>
          <Input data-testid="input-search-mensajes" placeholder="Buscar…" value={q} onChange={(e)=>setQ(e.target.value)} className="h-11 pl-10 bg-white border-[#E5DFD3]"/>
        </div>
      </div>

      <div className="space-y-2">
        {loading && <div className="p-10 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-[#7A1E2E]"/></div>}
        {!loading && filtered.length === 0 && (
          <div className="rounded-xl bg-white border-2 border-dashed border-[#E5DFD3] p-12 text-center">
            <MessageSquare className="h-8 w-8 mx-auto text-[#C25A5A]" strokeWidth={1.5}/>
            <div className="mt-3 font-display text-lg text-[#1A0810]">Sin mensajes</div>
            <div className="mt-1 text-xs text-[#6B5D57]">Envía un nuevo mensaje a algún miembro del equipo.</div>
          </div>
        )}
        {!loading && filtered.map((m) => {
          const isAsig = m.tipo === "asignacion";
          const noLeido = !m.es_remitente && !m.leido;
          return (
            <div
              key={m.id}
              data-testid={`mensaje-row-${m.id}`}
              onClick={() => marcarLeido(m)}
              className={`rounded-xl border p-4 flex items-start gap-4 hover:border-[#C25A5A] transition-colors cursor-pointer ${
                noLeido ? "bg-[#FBEDEF]/40 border-[#7A1E2E]/30" : "bg-white border-[#E5DFD3]"
              }`}
              style={{ borderLeft: `4px solid ${isAsig ? "#7A1E2E" : "#4A6D8C"}` }}
            >
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: isAsig ? "#FBEDEF" : "#DDE9F3" }}
              >
                {isAsig
                  ? <ClipboardList className="h-4 w-4" strokeWidth={1.8} style={{ color: "#7A1E2E" }}/>
                  : <MessageSquare className="h-4 w-4" strokeWidth={1.8} style={{ color: "#4A6D8C" }}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[15px] font-semibold text-[#1A0810]">{m.titulo}</div>
                  {isAsig && <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full bg-[#FBEDEF] text-[#7A1E2E] border border-[#7A1E2E]/25">Asignación</span>}
                  {noLeido && <span className="text-[10px] font-bold uppercase tracking-wider text-[#DC2626]">● No leído</span>}
                </div>
                {m.contenido && <div className="mt-1 text-xs text-[#4a3838]">{m.contenido}</div>}
                <div className="mt-2 text-[11px] text-[#6B5D57] flex flex-wrap gap-2">
                  <span>De: <b className="text-[#3d2a2a]">{m.remitente_nombre}</b></span>
                  <span>·</span>
                  <span>Para: <b className="text-[#3d2a2a]">{(m.destinatarios_nombres || []).join(", ")}</b></span>
                  <span>·</span>
                  <span>{fmtWhen(m.fecha)}</span>
                </div>
                {m.solicitud_id && (
                  <button
                    onClick={(e)=>{e.stopPropagation(); navigate("/solicitudes");}}
                    className="mt-1 text-[11px] uppercase tracking-wider font-semibold text-[#7A1E2E] hover:underline"
                  >
                    Ver solicitud →
                  </button>
                )}
              </div>
              {m.es_remitente && (
                <button
                  data-testid={`del-mensaje-${m.id}`}
                  onClick={(e)=>{e.stopPropagation(); eliminar(m);}}
                  className="h-8 w-8 rounded-md hover:bg-red-50 flex items-center justify-center text-[#6B5D57] hover:text-red-700 flex-shrink-0"
                  title="Eliminar"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.8}/>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Nuevo mensaje */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl bg-[#FAF7F0] border-[#E5DFD3] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A0810]">Nuevo mensaje</DialogTitle>
            <DialogDescription className="text-[#6B5D57]">Envía un mensaje interno a uno o varios miembros del equipo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Título *</Label>
              <Input data-testid="msg-titulo" value={form.titulo} onChange={(e)=>setForm({...form, titulo: e.target.value})} className={`mt-2 ${inputCls}`} placeholder="Ej: Revisión urgente del expediente"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Contenido</Label>
              <Textarea data-testid="msg-contenido" value={form.contenido} onChange={(e)=>setForm({...form, contenido: e.target.value})} className="mt-2 bg-white border-[#E5DFD3] min-h-[100px]" placeholder="Detalles…"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Destinatarios * ({form.destinatarios.length})</Label>
              <div className="mt-2 rounded-md border border-[#E5DFD3] bg-white p-3 max-h-[220px] overflow-y-auto space-y-2">
                {users.filter((u) => u.id !== user?.id).map((u) => (
                  <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-[#F5EEE0] p-1.5 rounded">
                    <Checkbox
                      data-testid={`msg-dest-${u.id}`}
                      checked={form.destinatarios.includes(u.id)}
                      onCheckedChange={() => toggleDest(u.id)}
                    />
                    <span className="text-sm text-[#1A0810]">{u.name || u.email}</span>
                    <span className="text-[11px] text-[#6B5D57]">· {u.role}</span>
                  </label>
                ))}
                {users.filter((u) => u.id !== user?.id).length === 0 && (
                  <div className="text-xs text-[#6B5D57] p-2">No hay otros usuarios registrados.</div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDialog(false)} className="h-10 border-[#E5DFD3]">Cancelar</Button>
            <Button data-testid="btn-enviar-mensaje" onClick={enviar} disabled={saving} className="h-10 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Send className="h-4 w-4 mr-2" strokeWidth={2}/>}
              Enviar mensaje
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
